<template>
  <KioskLayout :isCentered="true">

    <template #KioskLayout__top-right>
      <template></template>
    </template>

    <div class="CustomerPhotoFromPhone" data-testid="customer-phone-root">

      <h1 class="kiosk-title" v-if="!hasUploaded && !uploadError">
        {{ $t('CustomerPhotoFromPhone.title') }}
      </h1>

      <div class="CustomerPhotoFromPhone__button" v-if="!isUploading && !hasUploaded && !uploadError">
        <input class="CustomerPhotoFromPhone__button-input"
          type="file"
          multiple
          :accept="allowedFormats.join(',')"
          @change="onFileSelect"/>
        <button class="kiosk-button" data-testid="customer-phone-select-button">{{ $t('CustomerPhotoFromPhone.buttonSelect') }}</button>
      </div>

      <div v-if="!isUploading && !hasUploaded && !uploadError">
        {{ $t('CustomerPhotoFromPhone.maxCount', { count: MAX_PHOTO_COUNT }) }}
      </div>

      <div class="CustomerPhotoFromPhone__uploading kiosk-uppercase" v-if="isUploading" data-testid="customer-phone-uploading">
        <i class="CustomerPhotoFromPhone__uploading-spinner kiosk-spinner"/>
        {{ $t('CustomerPhotoFromPhone.uploading') }}
      </div>

      <div class="CustomerPhotoFromPhone__error" v-if="uploadError" data-testid="customer-phone-error">
        <div class="CustomerPhotoFromPhone__error-message">{{ $t('CustomerPhotoFromPhone.uploadError') || 'Upload failed. Please try again.' }}</div>
        <button class="kiosk-button CustomerPhotoFromPhone__error-retry" @click="onRetry">
          {{ $t('CustomerPhotoFromPhone.buttonRetry') || 'Try Again' }}
        </button>
      </div>

      <div class="CustomerPhotoFromPhone__success" v-if="hasUploaded" data-testid="customer-phone-success">
        <i class="CustomerPhotoFromPhone__success-icon kiosk-success-icon"/>
        <h1 class="CustomerPhotoFromPhone__success-title kiosk-title">{{ $t('CustomerPhotoFromPhone.successTitle') }}</h1>
        <div class="CustomerPhotoFromPhone__success-caption">{{ $t('CustomerPhotoFromPhone.successCaption') }}</div>
      </div>

    </div>
  </KioskLayout>
</template>


<script>
import { initialize } from '@/app/i18n';
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { ERROR_FALLBACKS, useUiStore } from '@/app/stores/ui';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { faceDetection } from '../../services/FaceDetection';
import { kioskConfig } from '../../services/KioskConfig';
import { photoProcessing } from '../../services/PhotoProcessing';
import { session } from '../../services/Session';

export default {
  setup() {
    return { ui: useUiStore() };
  },
  components: { KioskLayout },
  data() {
    return {
      MAX_PHOTO_COUNT: 3,
      isUploading: false,
      hasUploaded: false,
      uploadError: false,
      // Guard against post-unmount state mutations from async upload promise chain
      _isUnmounted: false,
      // Session ID from the QR code URL, validated during mount
      _sessionId: null,
      // Flag to track whether session initialization succeeded
      _sessionReady: false,
    };
  },
  computed: {
    allowedFormats() {
      return Object.values(photoProcessing.ALLOWED_FORMATS).flat();
    },
  },
  mounted() {
    exposeToGlobalScope(this);
    this._isUnmounted = false;
    this._sessionId = router.query.sessionId || null;

    sentryService.addBreadcrumb(
      'customer.upload',
      'Mobile upload screen opened',
      {
        // Full session ID is required to join customer upload telemetry to
        // backend/Postgres rows.
        session_id: this._sessionId,
      }
    );

    if (this._sessionId) {
      const loadingIndicator = this.ui.createLoadingIndicator();

      session
        .continue(this._sessionId)
        .then((sessionData) => {
          // Guard: don't update state after unmount
          if (this._isUnmounted) return;

          if (!sessionData.source) {
            return this.ui.showError(
              'photo_phone_missing_kiosk_slug',
              sessionData,
              ERROR_FALLBACKS.RELOAD_PAGE
            );
          }
          return kioskConfig
            .loadBranding(sessionData.source)
            .then(({ kiosk }) => {
              if (this._isUnmounted) return;

              // Check if mobile upload is disabled for this kiosk
              // Only hide if explicitly true, otherwise show (respect DB setting)
              const isMobileUploadHidden =
                store.kiosk?.hide_mobile_upload === true;
              if (isMobileUploadHidden) {
                return this.ui.showError(
                  'mobile_upload_disabled',
                  new Error('Mobile upload is not available for this kiosk'),
                  ERROR_FALLBACKS.CLOSE
                );
              }

              initialize(
                sessionData.language_selected || sessionData.language_initial,
                kiosk.languages
              );
              // initialize() is synchronous; mark session ready inline
              if (!this._isUnmounted) {
                this._sessionReady = true;
              }
              return undefined;
            });
        })
        .catch((e) => {
          if (this._isUnmounted) return;
          // Show error to user so they see something instead of a blank screen
          this.uploadError = true;
          sentryService.captureException(e, {
            tags: {
              component: 'CustomerPhotoFromPhone',
              operation: 'session_continue',
            },
            level: 'warning',
          });
        })
        .finally(() => {
          loadingIndicator.hide();
        });
    } else {
      this.ui.showError(
        'photo_phone_missing_session_id',
        router.query,
        ERROR_FALLBACKS.RELOAD_PAGE
      );
    }
  },
  beforeUnmount() {
    this._isUnmounted = true;
    this._sessionReady = false;
  },
  methods: {
    onRetry() {
      this.uploadError = false;
      this.isUploading = false;
      this.hasUploaded = false;
      // Reset the file input so the user can re-select files
      const fileInput = this.$el?.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
    },

    onFileSelect(e) {
      // Prevent double upload: block if already uploading or already uploaded
      if (this.isUploading || this.hasUploaded) return;

      // Validate session is still active before attempting upload
      if (!(this._sessionReady && store.sessionId)) {
        sentryService.addBreadcrumb(
          'customer.upload',
          'Upload blocked: session not ready',
          {
            session_ready: this._sessionReady,
            has_session_id: Boolean(store.sessionId),
          },
          'warning'
        );
        this.uploadError = true;
        return;
      }

      this.uploadError = false;
      const fileCount = e.target.files?.length || 0;
      sentryService.addBreadcrumb(
        'customer.upload',
        'Files selected for upload',
        {
          file_count: fileCount,
          max_allowed: this.MAX_PHOTO_COUNT,
        }
      );

      if (fileCount === 0) {
        return;
      }
      if (e.target.files.length > this.MAX_PHOTO_COUNT) {
        return this.ui.showError(
          'photo_phone_limit_exceeded',
          null,
          ERROR_FALLBACKS.CLOSE
        );
      }

      this.isUploading = true;

      photoProcessing
        .getPreparedBlobFromMultipleFiles(e.target.files)
        .then((blobs) => {
          if (this._isUnmounted) return;
          faceDetection.addPhotosFromMobile(blobs);
        })
        .then(() => {
          if (this._isUnmounted) return;
          const photosToUpload = faceDetection.queryPhotos({
            mobile: true,
            uploaded: false,
          });
          return faceDetection.upload(photosToUpload);
        })
        .then(() => {
          if (this._isUnmounted) return;
          this.hasUploaded = true;
          sentryService.addBreadcrumb(
            'customer.upload',
            'Mobile photos uploaded successfully',
            {
              file_count: fileCount,
            }
          );
        })
        .catch((uploadErr) => {
          if (this._isUnmounted) return;
          this.uploadError = true;
          sentryService.captureException(uploadErr, {
            tags: {
              component: 'CustomerPhotoFromPhone',
              operation: 'photo_upload',
            },
            extra: {
              file_count: fileCount,
            },
          });
        })
        .finally(() => {
          if (this._isUnmounted) return;
          this.isUploading = false;
        });
    },
  },
};
</script>


<style scoped lang="scss">
  .CustomerPhotoFromPhone {
    max-width: 32rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: var(--customer-photo-from-phone-font-size, 1.8rem);
  }

  .CustomerPhotoFromPhone__button {
    position: relative;
    width: var(--customer-photo-from-phone-button-width, auto);
    margin: var(--customer-photo-from-phone-button-margin, 2rem 0);

    .kiosk-button {
      width: var(--customer-photo-from-phone-button-control-width, min-content);
    }
  }

  .CustomerPhotoFromPhone__button-input {
    appearance: none;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  .CustomerPhotoFromPhone__uploading {
    margin-top: 2rem;
    opacity: 0.7;
  }

  .CustomerPhotoFromPhone__uploading-spinner {
    --kiosk-spinner-size: 1.1rem;
    --kiosk-spinner-color: var(--Kiosk--font_color);
    --kiosk-spinner-opacity: 1;
    --kiosk-spinner-thinkness: 0.25;

    margin: 0 0.7rem 0 0.2rem;
    display: inline-block;
  }

  .CustomerPhotoFromPhone__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;
  }

  .CustomerPhotoFromPhone__error-message {
    color: #FA5F42;
    text-align: center;
    margin-bottom: 1.5rem;
    font-size: 1.1em;
  }

  .CustomerPhotoFromPhone__error-retry {
    min-width: 12rem;
  }

  .CustomerPhotoFromPhone__success {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .CustomerPhotoFromPhone__success-icon {
    --kiosk-success-icon-size: var(
      --customer-photo-from-phone-success-icon-size,
      3rem
    );
  }

  .CustomerPhotoFromPhone__success-title {
    margin: var(
      --customer-photo-from-phone-success-title-margin,
      1.3rem 0 1rem 0
    );
  }

  .CustomerPhotoFromPhone__success-caption {
    max-width: var(
      --customer-photo-from-phone-success-caption-max-width,
      70%
    );
    text-align: center;
  }
</style>
